import { ReactNode } from "react";
import { Button, Icon, Input, DataDisplayProvider, Switch } from "@aidkitorg/component-library";
import { Translations } from "@aidkitorg/i18n/lib";
import { useDebouncedMemo } from "../Hooks/Debounce";

/**
 * Highlights all occurrences of a search term within text by wrapping matches in a yellow background span.
 * Case-insensitive matching with proper regex escaping for special characters.
 * @param text - The text to search within and highlight matches
 * @param searchTerm - The term to search for and highlight
 * @returns JSX element with highlighted matches
 */
export function highlightText(text?: string, searchTerm?: string): JSX.Element {
  if (!text) return <></>;
  if (!searchTerm) return <>{text}</>;
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? <span key={index} className="bg-yellow-100">{part}</span> : part
      )}
    </>
  );
}

/**
 * Formats a value for display by pretty-printing JSON objects and handling various data types.
 * Attempts to parse string values as JSON and format them with indentation.
 * @param value - The value to format (can be object, string, number, etc.)
 * @returns Object containing the formatted string and a flag indicating if it's JSON
 */
export function getPrettyValue(value: any): { pretty: string; isJSON: boolean } {
  try {
    if (typeof value === 'object') {
      return { pretty: JSON.stringify(value, null, 2), isJSON: true };
    }
    if (isNaN(Number(value))) {
      const parsed = JSON.stringify(JSON.parse(value), null, 2);
      return { pretty: parsed, isJSON: true };
    } else {
      return { pretty: String(value), isJSON: false };
    }
  } catch (e) {
    return { pretty: String(value ?? ''), isJSON: false };
  }
}

/**
 * Debounces search input and only returns values that are empty or meet the minimum length requirement.
 * This prevents searches with very short input that would return too many results.
 * @param value - The search input value to debounce
 * @param delay - The delay in milliseconds (default: 200)
 * @param minLength - Minimum length required for non-empty searches (default: 3)
 * @returns The search term that should be used for filtering
 */
export function useDebouncedSearch(value: string, delay: number = 200, minLength: number = 3): string {
  return useDebouncedMemo(
    () => {
      if (!value || value.length >= minLength) {
        return value;
      }
      return '';
    },
    delay,
    [value, minLength]
  );
}

/**
 * Filters out error fields that have never had a non-empty value.
 * This keeps track of fields which end in _error and filters out those that are always empty.
 * @param items - Array of history items to filter
 * @param allItems - All items (used to check if error field ever had a value)
 * @param hideEmptyErrors - Whether to apply the filter
 * @returns Filtered array
 */
export function filterEmptyErrors<T extends { key: string; value: any }>(
  items: T[],
  allItems: T[],
  hideEmptyErrors: boolean
): T[] {
  if (!hideEmptyErrors) {
    return items;
  }

  // This keeps track of fields which end in _error and have never had a non-empty value
  const alwaysEmptyErrors: Record<string, boolean> = {};
  for (const item of allItems) {
    if (item.key.endsWith('_error')) {
      if (item.value) {
        alwaysEmptyErrors[item.key] = false;
      } else {
        // Only overwrite undefined values
        alwaysEmptyErrors[item.key] ??= true;
      }
    }
  }

  // If we want to hide always empty errors, then refilter with the map we constructed above.
  // !== true will cover undefined, and false
  return items.filter((historyItem: T) => alwaysEmptyErrors[historyItem.key] !== true);
}

/**
 * Search bar component with input, clear button, and filter toggle button
 */
export function HistorySearchBar(props: {
  searchInput: string;
  setSearchInput: (value: string) => void;
  searchTerm: string;
  showSearchOptions: boolean;
  setShowSearchOptions: (show: boolean) => void;
  L: Translations;
}) {
  return (
    <div className="mb-4">
      <div className="flex-grow mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {props.L.dashboard.search}
        </label>
        <div className="flex items-center relative">
          <input
            type="text"
            className="flex-grow p-1 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder={props.L.dashboard.search}
            value={props.searchInput}
            onChange={(e) => props.setSearchInput(e.target.value)}
          />
          {props.searchTerm && (
            <Button
              className="absolute !p-0 right-8"
              aria-label="Clear search terms"
              variant="unstyled"
              icon={
                <Icon
                  name="XCircleIcon"
                  role="decorative"
                />
              }
              onClick={() => { props.setSearchInput(''); }}
            />
          )}
          <Button
            className="!p-0.5"
            variant="unstyled"
            aria-label="Filter"
            icon={
              <Icon
                name="FunnelIcon"
                role="decorative"
              />
            }
            onClick={() => props.setShowSearchOptions(!props.showSearchOptions)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Filter options panel component with date inputs and checkboxes
 */
export function HistoryFilterOptions(props: {
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  exactMatch: boolean;
  setExactMatch: (value: boolean) => void;
  searchValue: boolean;
  setSearchValue: (value: boolean) => void;
  searchTargetField: boolean;
  setSearchTargetField: (value: boolean) => void;
  searchAuthor?: boolean;
  setSearchAuthor?: (value: boolean) => void;
  onlyShowLatest?: boolean;
  setOnlyShowLatest?: (value: boolean) => void;
  showEmptyFields?: boolean;
  setShowEmptyFields?: (value: boolean) => void;
  hideEmptyErrors: boolean;
  setHideEmptyErrors: (value: boolean) => void;
  L: Translations;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input
          type="date"
          label={props.L.applicant.history.start_date}
          value={props.startDate}
          onChange={(e) => props.setStartDate(e.target.value)}
          className="w-full"
        />
        <Input
          type="date"
          label={props.L.applicant.history.end_date}
          value={props.endDate}
          onChange={(e) => props.setEndDate(e.target.value)}
          className="w-full"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        <div className="flex items-center cursor-pointer">
          <input
            id="exactMatch"
            type="checkbox"
            className="h-4 w-4 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
            checked={props.exactMatch}
            onChange={() => props.setExactMatch(!props.exactMatch)}
          />
          <label htmlFor="exactMatch" className="ml-2 mb-0  text-gray-700 cursor-pointer">
            {props.L.applicant.history.exact_match}
          </label>
        </div>
        <div className="flex items-center cursor-pointer">
          <input
            id="searchValue"
            type="checkbox"
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
            checked={props.searchValue}
            onChange={() => props.setSearchValue(!props.searchValue)}
          />
          <label htmlFor="searchValue" className="ml-2 mb-0  text-gray-700 cursor-pointer">
            {props.L.applicant.history.search_values}
          </label>
        </div>
        <div className="flex items-center cursor-pointer">
          <input
            id="searchTargetField"
            type="checkbox"
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
            checked={props.searchTargetField}
            onChange={() => props.setSearchTargetField(!props.searchTargetField)}
          />
          <label htmlFor="searchTargetField" className="ml-2 mb-0  text-gray-700 cursor-pointer">
            {props.L.applicant.history.search_target_fields}
          </label>
        </div>
        {props.searchAuthor !== undefined && props.setSearchAuthor && (
          <div className="flex items-center cursor-pointer">
            <input
              id="searchAuthor"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
              checked={props.searchAuthor}
              onChange={() => props.setSearchAuthor!(!props.searchAuthor)}
            />
            <label htmlFor="searchAuthor" className="ml-2 mb-0  text-gray-700 cursor-pointer">
              {props.L.applicant.history.search_authors}
            </label>
          </div>
        )}
        {props.onlyShowLatest !== undefined && props.setOnlyShowLatest && (
          <div className="flex items-center cursor-pointer">
            <input
              id="onlyShowLatest"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
              checked={props.onlyShowLatest}
              onChange={() => props.setOnlyShowLatest!(!props.onlyShowLatest)}
            />
            <label htmlFor="onlyShowLatest" className="ml-2 mb-0 text-gray-700 cursor-pointer">
              {props.L.applicant.history.only_show_latest}
            </label>
          </div>
        )}
        <div className="flex items-center cursor-pointer">
          <input
            id="showEmptyFields"
            type="checkbox"
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
            checked={props.showEmptyFields}
            onChange={() => props.setShowEmptyFields?.(!props.showEmptyFields)}
          />
          <label htmlFor="showEmptyFields" className="ml-2 mb-0 text-gray-700 cursor-pointer">
            {props.L.applicant.history.show_empty_fields}
          </label>
        </div>
        <div className="flex items-center cursor-pointer">
          <input
            id="hideEmptyErrors"
            type="checkbox"
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
            checked={props.hideEmptyErrors}
            onChange={() => props.setHideEmptyErrors(!props.hideEmptyErrors)}
          />
          <label htmlFor="hideEmptyErrors" className="ml-2 mb-0 text-gray-700 cursor-pointer">
            {props.L.applicant.history.hide_empty_errors}
          </label>
        </div>
      </div>
    </div>
  );
}

/**
 * History list wrapper component with UTC toggle and DataDisplayProvider
 */
export function HistoryListWrapper(props: {
  useUTC: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="overflow-hidden">
      <ul className="">
        <DataDisplayProvider value={{ displayInUTC: props.useUTC }}>
          <div className='px-1 py-2'>
            <Switch label="Display time in UTC" onCheckedChange={props.onCheckedChange} />
          </div>
          {props.children}
        </DataDisplayProvider>
      </ul>
      {props.footer}
    </div>
  );
}
